import type { ReactNode } from "react";
import { ChevronDown, ChevronUp, Loader2, RefreshCw, X } from "lucide-react";
import { FranklinSpeechBubble } from "@/components/chat/SidebarChat/FranklinSpeechBubble";
import { BottomRightLaneSlot } from "@/components/layout/BottomRightLane";
import { Button } from "@/components/ui/button";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import type { useUploadQueueStore } from "@/hooks/useUploadQueueStore";
import type { DocumentUploadQueueTranslations } from "@/lib/i18n/types/documents-sections/upload";
import { cn } from "@/lib/utils";
import { FailedUploadsPanel } from "./FailedUploadsPanel";

type UploadQueueStore = ReturnType<typeof useUploadQueueStore>;

export interface Segment {
  count: number;
  className: string;
}

function SegmentedBar({ segments, className }: { segments: Segment[]; className?: string }) {
  const visible = segments.filter((segment) => segment.count > 0);
  return (
    <div className={cn("flex h-2 w-full overflow-hidden rounded-full bg-neutral-200", className)}>
      {visible.map((segment, index) => (
        <div
          key={index}
          className={cn("h-full transition-all", segment.className)}
          style={{ flex: segment.count }}
        />
      ))}
    </div>
  );
}

function LegendItem({
  dotClassName,
  count,
  label,
}: {
  dotClassName: string;
  count: number;
  label: string;
}) {
  return (
    <span className="flex items-center gap-1.5 text-neutral-600">
      <span className={cn("h-2 w-2 shrink-0 rounded-full", dotClassName)} />
      {count} {label}
    </span>
  );
}

export function StatusControls({
  isMinimized,
  onToggle,
  onDismiss,
  expandLabel,
  minimizeLabel,
  dismissLabel,
}: {
  isMinimized: boolean;
  onToggle: () => void;
  onDismiss: () => void;
  expandLabel: string;
  minimizeLabel: string;
  dismissLabel: string;
}) {
  const ToggleIcon = isMinimized ? ChevronUp : ChevronDown;
  const toggleLabel = isMinimized ? expandLabel : minimizeLabel;

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        variant="ghost"
        size="sm"
        onClick={onToggle}
        className="h-7 w-7 p-0 text-neutral-500 hover:text-neutral-800"
        title={toggleLabel}
        aria-label={toggleLabel}
      >
        <ToggleIcon className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDismiss}
        className="h-7 w-7 p-0 text-neutral-500 hover:text-neutral-800"
        title={dismissLabel}
        aria-label={dismissLabel}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

function FranklinStatusHeading({ children }: { children: string }) {
  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <FranklinLogo size="xs" showBorder={false} className="-mt-0.5" />
      <p className="min-w-0 text-sm font-medium leading-5 text-neutral-800">{children}</p>
    </div>
  );
}

interface MinimizedStatusBubbleProps {
  showProcessing: boolean;
  processingCount: number;
  overallProgress: number;
  controls: ReactNode;
  copy: Pick<DocumentUploadQueueTranslations, "processingShort" | "receivingShort">;
}

export function MinimizedStatusBubble(props: MinimizedStatusBubbleProps) {
  const { showProcessing, processingCount, overallProgress, controls, copy } = props;
  const summary = showProcessing
    ? copy.processingShort.replace("{count}", String(processingCount))
    : copy.receivingShort;

  return (
    <BottomRightLaneSlot name="uploadQueue">
      <FranklinSpeechBubble
        role="status"
        aria-live="polite"
        data-testid="franklin-upload-status"
        className="p-2.5"
      >
        <div className="flex items-center gap-2">
          <FranklinLogo size="xs" showBorder={false} />
          <span className="min-w-0 flex-1 truncate text-sm text-neutral-700">{summary}</span>
          {showProcessing ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-info" />
          ) : (
            <span className="shrink-0 text-xs font-medium text-neutral-600">
              {overallProgress}%
            </span>
          )}
          {controls}
        </div>
      </FranklinSpeechBubble>
    </BottomRightLaneSlot>
  );
}

function UploadLegend({
  uploadStore,
  copy,
  hasFailures,
}: {
  uploadStore: UploadQueueStore;
  copy: DocumentUploadQueueTranslations;
  hasFailures: boolean;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      {uploadStore.completedCount > 0 && (
        <LegendItem
          dotClassName="bg-success"
          count={uploadStore.completedCount}
          label={copy.done}
        />
      )}
      {uploadStore.uploadingCount > 0 && (
        <LegendItem
          dotClassName="bg-primary"
          count={uploadStore.uploadingCount}
          label={copy.uploading}
        />
      )}
      {uploadStore.pendingCount > 0 && (
        <LegendItem
          dotClassName="bg-neutral-300"
          count={uploadStore.pendingCount}
          label={copy.pending}
        />
      )}
      {hasFailures && (
        <LegendItem
          dotClassName="bg-destructive"
          count={uploadStore.failedCount}
          label={copy.failed}
        />
      )}
      {uploadStore.skippedCount > 0 && (
        <LegendItem
          dotClassName="bg-warning"
          count={uploadStore.skippedCount}
          label={copy.skipped}
        />
      )}
    </div>
  );
}

function UploadFailureDetails({
  uploadStore,
  copy,
  onRetry,
}: {
  uploadStore: UploadQueueStore;
  copy: DocumentUploadQueueTranslations;
  onRetry: () => void;
}) {
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="mt-3 w-full"
        disabled={uploadStore.isProcessing && !uploadStore.isComplete}
      >
        <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
        {copy.retryFailed.replace("{count}", String(uploadStore.failedCount))}
      </Button>
      <div className="mt-3">
        <FailedUploadsPanel />
      </div>
    </>
  );
}

function ProcessingDetail({ text }: { text: string }) {
  return (
    <div className="mt-3 flex items-center gap-2 border-t border-neutral-100 pt-3">
      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-info" />
      <span className="text-xs font-medium text-neutral-700">{text}</span>
    </div>
  );
}

interface ProgressSummaryProps {
  completed: number;
  total: number;
  progressPercent: number;
  segments: Segment[];
  copy: DocumentUploadQueueTranslations;
}

function ProgressSummary(props: ProgressSummaryProps) {
  const { completed, total, progressPercent, segments, copy } = props;
  return (
    <>
      <div className="mt-3 flex items-baseline justify-between border-t border-neutral-100 pt-3">
        <span className="text-sm font-semibold text-neutral-800">
          {copy.filesProgress
            .replace("{completed}", String(completed))
            .replace("{total}", String(total))}
        </span>
        <span className="text-sm font-semibold text-neutral-800">{progressPercent}%</span>
      </div>
      <SegmentedBar segments={segments} className="mt-2" />
    </>
  );
}

interface ProcessingStatusBubbleProps {
  message: string;
  controls: ReactNode;
  progressCompleted: number;
  progressTotal: number;
  progressPercent: number;
  segments: Segment[];
  processingDetail: string;
  subscribed: boolean;
  copy: DocumentUploadQueueTranslations;
  onSubscribe: () => void;
}

interface ProcessingCompletionActionProps {
  subscribed: boolean;
  copy: DocumentUploadQueueTranslations;
  onSubscribe: () => void;
}

function ProcessingCompletionAction(props: ProcessingCompletionActionProps) {
  const { subscribed, copy, onSubscribe } = props;
  return (
    <button
      type="button"
      onClick={onSubscribe}
      disabled={subscribed}
      aria-pressed={subscribed}
      className="mt-3 text-left text-sm font-medium text-primary transition-colors hover:text-primary/80 disabled:cursor-default disabled:text-neutral-500"
    >
      {subscribed ? copy.notifySubscribed : copy.notifyWhenDone}
    </button>
  );
}

export function ProcessingStatusBubble(props: ProcessingStatusBubbleProps) {
  const {
    message,
    controls,
    progressCompleted,
    progressTotal,
    progressPercent,
    segments,
    processingDetail,
    subscribed,
    copy,
    onSubscribe,
  } = props;
  return (
    <BottomRightLaneSlot name="uploadQueue">
      <FranklinSpeechBubble
        role="status"
        aria-live="polite"
        data-testid="franklin-processing-status"
      >
        <div className="flex items-start justify-between gap-2">
          <FranklinStatusHeading>{message}</FranklinStatusHeading>
          {controls}
        </div>
        <ProgressSummary
          completed={progressCompleted}
          total={progressTotal}
          progressPercent={progressPercent}
          segments={segments}
          copy={copy}
        />
        <ProcessingDetail text={processingDetail} />
        <ProcessingCompletionAction subscribed={subscribed} copy={copy} onSubscribe={onSubscribe} />
      </FranklinSpeechBubble>
    </BottomRightLaneSlot>
  );
}

interface ActiveUploadStatusBubbleProps {
  message: string;
  controls: ReactNode;
  uploadStore: UploadQueueStore;
  segments: Segment[];
  copy: DocumentUploadQueueTranslations;
  hasFailures: boolean;
  processingDetail: string | null;
  onRetry: () => void;
  onCancel: () => void;
}

export function ActiveUploadStatusBubble(props: ActiveUploadStatusBubbleProps) {
  const {
    message,
    controls,
    uploadStore,
    segments,
    copy,
    hasFailures,
    processingDetail,
    onRetry,
    onCancel,
  } = props;
  return (
    <BottomRightLaneSlot name="uploadQueue">
      <FranklinSpeechBubble role="status" aria-live="polite" data-testid="franklin-upload-status">
        <div className="flex items-start justify-between gap-2">
          <FranklinStatusHeading>{message}</FranklinStatusHeading>
          {controls}
        </div>
        <ProgressSummary
          completed={uploadStore.completedCount}
          total={uploadStore.totalCount}
          progressPercent={uploadStore.overallProgress}
          segments={segments}
          copy={copy}
        />
        <UploadLegend uploadStore={uploadStore} copy={copy} hasFailures={hasFailures} />
        {hasFailures && (
          <UploadFailureDetails uploadStore={uploadStore} copy={copy} onRetry={onRetry} />
        )}
        {processingDetail && <ProcessingDetail text={processingDetail} />}
        {!uploadStore.isComplete && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-3 text-xs font-medium text-destructive hover:text-destructive/80"
          >
            {copy.cancelAll}
          </button>
        )}
      </FranklinSpeechBubble>
    </BottomRightLaneSlot>
  );
}
